import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuccessModal = ({ isOpen, onClose, submissionData }) => {
  if (!isOpen || !submissionData) return null;

  const generateTicketNumber = () => {
    const timestamp = Date.now()?.toString()?.slice(-6);
    const random = Math.floor(Math.random() * 1000)?.toString()?.padStart(3, '0');
    return `CLN-${timestamp}-${random}`;
  };

  const ticketNumber = generateTicketNumber();
  const currentDate = new Date()?.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleWhatsAppFollow = () => {
    const message = encodeURIComponent(
      `Hi Clean360! I just submitted a contact form (Ticket: ${ticketNumber}). I'd like to continue our conversation here.`
    );
    window.open(`https://wa.me/15551234567?text=${message}`, '_blank');
  };

  const handleEmailCopy = () => {
    navigator.clipboard?.writeText('hello@clean360.com');
    alert('Email address copied to clipboard!');
  };

  const handleDownloadSummary = () => {
    const summaryText = `
Clean360 Contact Form Submission
Ticket Number: ${ticketNumber}
Submitted: ${currentDate}

Contact Information:
Name: ${submissionData?.name}
Email: ${submissionData?.email}
Phone: ${submissionData?.phone}

Service Details:
Inquiry Type: ${submissionData?.inquiryType}
Service Type: ${submissionData?.serviceType || 'N/A'}
Address: ${submissionData?.address || 'N/A'}
Urgency: ${submissionData?.urgency}
Preferred Contact: ${submissionData?.preferredContact}

Message:
${submissionData?.message}

Estimated Response Time: ${submissionData?.estimatedResponseTime}
    `;

    const blob = new Blob([summaryText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Clean360-Submission-${ticketNumber}.txt`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getUrgencyColor = () => {
    const colors = {
      'emergency': 'text-error',
      'high': 'text-warning',
      'normal': 'text-primary',
      'low': 'text-accent'
    };
    return colors?.[submissionData?.urgency] || 'text-primary';
  };

  const getUrgencyIcon = () => {
    const icons = {
      'emergency': 'AlertTriangle',
      'high': 'Clock',
      'normal': 'CheckCircle',
      'low': 'Calendar'
    };
    return icons?.[submissionData?.urgency] || 'CheckCircle';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={24} color="white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary">
                  Request Submitted!
                </h2>
                <p className="text-sm text-text-secondary">
                  {currentDate}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close success modal"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Ticket Information */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Ticket" size={20} color="var(--color-primary)" />
                <span className="font-medium text-primary">Ticket Number</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(ticketNumber)}
                iconName="Copy"
                iconSize={14}
              >
                Copy
              </Button>
            </div>
            <p className="text-lg font-mono font-bold text-primary">
              {ticketNumber}
            </p>
            <p className="text-sm text-primary/80 mt-1">
              Keep this number for your records
            </p>
          </div>

          {/* Response Time */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name={getUrgencyIcon()} size={20} color="var(--color-accent)" />
              <span className="font-medium text-accent">Expected Response</span>
            </div>
            <p className="text-lg font-semibold text-accent">
              {submissionData?.estimatedResponseTime}
            </p>
            <p className="text-sm text-accent/80">
              Based on your {submissionData?.urgency} priority level
            </p>
          </div>

          {/* Submission Summary */}
          <div className="space-y-4">
            <h3 className="font-semibold text-text-primary">Submission Summary</h3>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-secondary">Inquiry Type:</span>
                <p className="font-medium text-text-primary capitalize">
                  {submissionData?.inquiryType?.replace('-', ' ')}
                </p>
              </div>
              <div>
                <span className="text-text-secondary">Contact Method:</span>
                <p className="font-medium text-text-primary capitalize">
                  {submissionData?.preferredContact}
                </p>
              </div>
              {submissionData?.serviceType && (
                <div className="col-span-2">
                  <span className="text-text-secondary">Service:</span>
                  <p className="font-medium text-text-primary capitalize">
                    {submissionData?.serviceType?.replace('-', ' ')}
                  </p>
                </div>
              )}
              {submissionData?.files && submissionData?.files?.length > 0 && (
                <div className="col-span-2">
                  <span className="text-text-secondary">Attachments:</span>
                  <p className="font-medium text-text-primary">
                    {submissionData?.files?.length} file(s) uploaded
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-muted rounded-lg p-4">
            <h4 className="font-medium text-text-primary mb-3">What happens next?</h4>
            <div className="space-y-2 text-sm text-text-secondary">
              <div className="flex items-start space-x-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5" />
                <span>We'll review your request and assign it to the right team member</span>
              </div>
              <div className="flex items-start space-x-2">
                <Icon name="Phone" size={16} color="var(--color-primary)" className="mt-0.5" />
                <span>You'll receive a call or message within {submissionData?.estimatedResponseTime}</span>
              </div>
              <div className="flex items-start space-x-2">
                <Icon name="Calendar" size={16} color="var(--color-accent)" className="mt-0.5" />
                <span>We'll schedule your service at a convenient time</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleWhatsAppFollow}
                iconName="MessageSquare"
                iconPosition="left"
              >
                Continue on WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={handleEmailCopy}
                iconName="Mail"
                iconPosition="left"
              >
                Copy Email
              </Button>
            </div>
            
            <Button
              variant="outline"
              fullWidth
              onClick={handleDownloadSummary}
              iconName="Download"
              iconPosition="left"
            >
              Download Summary
            </Button>

            <Button
              variant="default"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
          </div>

          {/* Emergency Note */}
          {submissionData?.urgency === 'emergency' && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                <span className="font-medium text-error">Emergency Request</span>
              </div>
              <p className="text-sm text-error/80">
                For immediate assistance, please call us directly at (555) 123-4567. 
                Our emergency team is standing by 24/7.
              </p>
            </div>
          )}

          {/* Contact Information */}
          <div className="text-center pt-4 border-t border-border">
            <p className="text-sm text-text-secondary mb-2">
              Need immediate assistance?
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = 'tel:+15551234567'}
                iconName="Phone"
                iconPosition="left"
              >
                (555) 123-4567
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = 'mailto:hello@clean360.com'}
                iconName="Mail"
                iconPosition="left"
              >
                hello@clean360.com
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;